/**
 * Avatar source resolution (FRIDAY-48).
 *
 * Precedence: custom Friday avatar → auth-provider avatar → Gravatar →
 * initials. Gravatar URLs use the SHA-256 of the trimmed, lower-cased email
 * and `d=404`, so a missing Gravatar fails the image load instead of
 * returning a generated placeholder; the component then falls through to
 * initials. The raw email never appears in a URL.
 */

const GRAVATAR_BASE = 'https://gravatar.com/avatar/'

export function normalizeEmail(email: string | null | undefined): string | null {
  const normalized = email?.trim().toLowerCase() ?? ''
  return normalized.includes('@') ? normalized : null
}

export async function sha256Hex(value: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value))
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('')
}

const hashCache = new Map<string, Promise<string | null>>()

/** Gravatar URL for `email`, or null when the email is missing/invalid or hashing is unavailable. */
export function gravatarUrl(email: string | null | undefined, pixelSize: number): Promise<string | null> {
  const normalized = normalizeEmail(email)
  if (!normalized) return Promise.resolve(null)
  let hash = hashCache.get(normalized)
  if (!hash) {
    hash = typeof crypto !== 'undefined' && crypto.subtle
      ? sha256Hex(normalized).catch(() => null)
      : Promise.resolve(null)
    hashCache.set(normalized, hash)
  }
  const size = Math.max(1, Math.min(2048, Math.round(pixelSize)))
  return hash.then((h) => (h ? `${GRAVATAR_BASE}${h}?s=${size}&d=404` : null))
}

/** Ordered, de-duplicated image candidates; blank entries are dropped. */
export function avatarCandidates(sources: readonly (string | null | undefined)[]): string[] {
  const out: string[] = []
  for (const src of sources) {
    const trimmed = src?.trim()
    if (trimmed && !out.includes(trimmed)) out.push(trimmed)
  }
  return out
}

export function initialsFor(name: string): string {
  return (name.trim() || '?').slice(0, 2).toUpperCase()
}
